import React, { useState, useEffect, useRef } from 'react';
import DashboardHeader from '../include/DashboardHeader';
import DashboardSidebar from '../include/DashboardSidebar';
import DashboardTitle from './dashboard_modules/DashboardTitle';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap'
import { ToastContainer } from 'react-toastify'
import ProductData from './ProductData'
import useAxiosInstance from '../../Api/AxiosInstance';
import CheckoutPopup from '../include/CheckoutPopup';

function Shop() {
  const AxiosInstance = useAxiosInstance()

  /* ---------------------------------------------------------------------
     CART PANEL ON SHOP PAGE (NEW)
     Appears on the LEFT of the medicine grid, and only once the cart has
     something in it - exactly like the header cart badge.
     Data source and maths match Cart.js: 'cart-lists' for the items,
     'update-cart-lists' to change quantity.
     To go back to the plain grid, comment out this block, the cart markup
     below, and uncomment the original <ProductData/> markup at the bottom.
  --------------------------------------------------------------------- */
  const [cartitems, setCartitems] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);

  /* ---------------------------------------------------------------------
     IN-FLIGHT GUARDS (NEW)
     `removingId` / `updatingId` disable the button whose request is in
     flight, so a fast double-click cannot post the same
     'update-cart-lists' twice. The refs are the real guard: they flip
     synchronously, whereas state only settles on the next render - there
     is a window between `await` resolving and React re-rendering where a
     state-only guard would still let a second click through.

     The duplicate second POST is what the backend answered with 400
     ("Request failed with status code 400"), because the row was already
     at quantity 0 / already gone.
  --------------------------------------------------------------------- */
  const [removingId, setRemovingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const removingRef = useRef(false);
  const updatingRef = useRef(false);
  const previousRef = useRef(null);

  let sum = 0;
  let gst = 0;
  for (let i = 0; i < cartitems.length; i++) {
    sum += cartitems[i].sub_total
    gst += cartitems[i].tax_amount
  }

  const shipping = 0
  let total = sum + shipping + gst

  const fetchCart = async () => {
    try {
      const response = await AxiosInstance.post('cart-lists')
      setCartitems(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  /* ---------------------------------------------------------------------
     QUANTITY (CHANGED - was: await POST, then fetchCart())
     PREVIOUS:
       const quantityCount = async (data) => {
         await AxiosInstance.post('update-cart-lists', { cart_id: data[0], quantity: data[1] })
         fetchCart()
       };

     The second round trip is gone. The server response is NOT used because no
     code anywhere reads an 'update-cart-lists' response, so its shape is not
     something this file can rely on. Instead the row is moved in local state
     first and only reconciled by a background fetchCart() when the request
     fails.

     Subtotal/GST/tax are recomputed from the row we already have, so the
     totals in the footer stay consistent with the optimistic quantity. The tax
     rate is snapshotted from the current row rather than assumed to be a fixed
     percentage, so a row with no tax keeps no tax.
  --------------------------------------------------------------------- */
  const quantityCount = async (data) => {
    if (updatingRef.current) return;
    const [cart_id, quantity] = data;

    if (quantity <= 0) {
      // Same path as the Remove button - guard removeRef too so a click on
      // "-" at qty 1 cannot race a click on the trash icon for the same row.
      return removeItem(cart_id);
    }

    updatingRef.current = true;
    setUpdatingId(cart_id);
    try {
      await AxiosInstance.post('update-cart-lists', { cart_id: cart_id, quantity: quantity });
      setCartitems(prev => prev.map(item => {
        if (item.cart_id !== cart_id) return item;
        const unitPrice = item.quantity ? (item.sub_total / item.quantity) : item.cart_price;
        const taxRate = item.sub_total ? (item.tax_amount / item.sub_total) : 0;
        const newSubTotal = unitPrice * quantity;
        return {
          ...item,
          quantity: quantity,
          sub_total: newSubTotal,
          tax_amount: newSubTotal * taxRate,
        };
      }));
    } catch (error) {
      console.log(error);
      // Roll back to server truth rather than guessing the old numbers back.
      fetchCart();
    } finally {
      updatingRef.current = false;
      setUpdatingId(null);
    }
  };

  // There is no delete-cart route on the API, so "Remove" sets quantity to 0
  // through update-cart-lists. Items with quantity 0 are then filtered out
  // below, so removal shows instantly whether or not the backend drops the row.
  //
  // CHANGED - was: await POST, then fetchCart(). The row is now removed from
  // view immediately and only restored if the request rejects. PREVIOUS:
  //   const removeItem = async (cart_id) => {
  //     await AxiosInstance.post('update-cart-lists', { cart_id: cart_id, quantity: 0 })
  //     fetchCart()
  //   };
  const removeItem = async (cart_id) => {
    if (removingRef.current) return;
    removingRef.current = true;
    setRemovingId(cart_id);

    const previous = cartitems;
    // quantity 0 hides the row - `visibleItems` below filters on quantity > 0.
    setCartitems(prev => {
      // Capture `prev` (not the render-time `cartitems`) so a rollback restores
      // the state as it actually was when the request went out.
      previousRef.current = prev;
      return prev.map(item => (
        item.cart_id === cart_id ? { ...item, quantity: 0 } : item
      ));
    });

    try {
      await AxiosInstance.post('update-cart-lists', { cart_id: cart_id, quantity: 0 });
    } catch (error) {
      console.log(error);
      // Put the row back, then re-sync in case the failure was something else.
      setCartitems(previousRef.current || previous);
      fetchCart();
    } finally {
      removingRef.current = false;
      setRemovingId(null);
    }
  };

  useEffect(() => {
    fetchCart()
    // ProductData.js fires 'itemAddedToCart' on every add, so the panel
    // refreshes without a page reload (the header badge uses the same event).
    const onItemAdded = () => fetchCart();
    document.addEventListener('itemAddedToCart', onItemAdded);
    return () => document.removeEventListener('itemAddedToCart', onItemAdded);
  }, [])

  // Hide anything at quantity 0 so a removed item never lingers on screen.
  const visibleItems = cartitems.filter(item => item.quantity > 0)

  return (
    <div className='main-page bg4'>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <DashboardHeader />
      {/* ---------------------------------------------------------------
          HIDDEN AS REQUESTED: sidebar on the shop page.
          Only the dashboard keeps its own sidebar (DashboardSidebar is
          rendered by Dashboard.js). Uncomment to bring this one back.
      ---------------------------------------------------------------- */}
      {/* <DashboardSidebar /> */}
      {/* 'no-sidebar' cancels the 262px left offset that .main-section reserves
          for the fixed sidebar. DashboardSidebar is commented out on this page,
          so without it the content starts 262px in and leaves a blank gutter.
          CSS for it is in main.css (.main-section.no-sidebar).
          To restore the gutter, change the className back to 'main-section'. */}
      <section className='main-section no-sidebar'>
        <div className='main-inner'>
          <DashboardTitle />
          <div className='shop-page-main'>
            <div className='breadcrumb-block ptb-20 pt-0'>
              <ul className='breadcrumb-list d-flex v-center'>
                <li><Link to='/dashboard'>Home</Link></li>
                <li>Order medicine</li>
              </ul>
            </div>

            {/* ---------------------------------------------------------------
                CART PANEL + MEDICINE GRID (NEW)
                Cart column renders only when the cart is not empty.
                Grid takes the full width when there is no cart to show.
            ---------------------------------------------------------------- */}
            <Row className='cart-blocks'>
              {visibleItems.length !== 0 &&
                /* Narrowed from md/lg={4} (33%) to {3} (25%) so the medicine grid
                   gets the wider share of the row. The original props are kept in
                   the comment below - swap them back to restore the 33/66 split.
                   PREVIOUS: <Col xs={12} md={4} lg={4} className='left-block'> */
                <Col key='cart-panel' xs={12} md={3} lg={3} className='left-block'>
                  {/* COMPACT CART (NEW): shop-cart-* classes only, so the shared
                      .cart-items styles used by Cart.js / Checkout.js are untouched.
                      Heading is sticky, .shop-cart-scroll scrolls at 350px. */}
                  <div className='white_box shop-cart-panel'>
                    <div className='box-title'>
                      <h4>Your Cart</h4>
                    </div>

                    <div className='shop-cart-scroll'>
                      {visibleItems.map((item, index) =>
                        <div className='shop-cart-row' key={index}>
                          <div className='shop-cart-thumb'>
                            <img src={item.image} alt='' />
                          </div>

                          <div className='shop-cart-details'>
                            <p className='shop-cart-name' title={item.name}>{item.name}</p>

                            <div className='shop-cart-meta'>
                              <span className='shop-cart-unit'>₹ {item.cart_price.toFixed(2)}</span>

                              <div className='shop-cart-qty'>
                                <button type='button' disabled={removingId === item.cart_id || updatingId === item.cart_id} onClick={() => quantityCount([item.cart_id, item.quantity - 1])}>-</button>
                                <span>{item.quantity}</span>
                                <button type='button' disabled={removingId === item.cart_id || updatingId === item.cart_id} onClick={() => quantityCount([item.cart_id, item.quantity + 1])}>+</button>
                              </div>

                              <span className='shop-cart-subtotal'>₹ {item.sub_total.toFixed(2)}</span>

                              <button type='button' className='shop-cart-remove' title='Remove' disabled={removingId === item.cart_id || updatingId === item.cart_id} onClick={() => removeItem(item.cart_id)}>
                                <i className="las la-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* CART TOTAL (NEW): lives inside the panel, below the
                        scroller, so it stays pinned and visible while the item
                        list scrolls. Footer of the flex column, never scrolls. */}
                    <div className='shop-cart-footer'>
                      <div className='box-title'>
                        <h4>Cart Total</h4>
                      </div>

                      <div className='cart-total-list'>
                        <div className='sub-item d-flex'>
                          <label className="cart-label">Subtotal</label>
                          <span className='cart-value'>₹ {sum.toFixed(2)}</span>
                        </div>
                        <div className='sub-item d-flex'>
                          <label className="cart-label">GST</label>
                          <span className='cart-value'>₹ {gst.toFixed(2)}</span>
                        </div>
                        {/*<div className='sub-item d-flex'>
                          <label className="cart-label">Shipping and Handing</label>
                          <span className='cart-value'>₹ {shipping.toFixed(2)}</span>
                        </div>*/}
                        <div className='sub-item d-flex grand-total-item'>
                          <label className="cart-label"><strong>Total</strong></label>
                          <span className='cart-value'><strong>₹ {total.toFixed(2)}</strong></span>
                        </div>
                        <div className='sub-item d-flex grand-total-item'>
                          <span className='cart-label'>*Shipping fees will apply according to your delivery address.*</span>
                        </div>
                        <div className='sub-item d-flex form-btnbox'>
                          {/* CHANGED: opens the checkout popup instead of navigating
                              to /checkout. PREVIOUS (kept for restore):
                              <Link className='btn secondary w-100' to='/checkout/'>Proceed to checkout <i className="las la-shopping-cart ml-2"></i></Link> */}
                          <button type='button' className='btn secondary w-100' onClick={() => setShowCheckout(true)}>Proceed to checkout <i className="las la-shopping-cart ml-2"></i></button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ---------------------------------------------------------------
                      PREVIOUS CART MARKUP - kept for easy restore.
                      Uncomment this block and comment out the compact
                      <div className='white_box shop-cart-panel'> above to go back
                      to the roomy labelled layout.
                  ---------------------------------------------------------------- */}
                  {/*
                  <div className='white_box cart-list-wrapper'>
                    <div className='box-title'>
                      <h4>Your Cart</h4>
                    </div>

                    {visibleItems.map((item, index) =>
                      <div className='cart-items d-flex v-center' key={index}>
                        <div className='cart-imgbox d-flex'>
                          <img src={item.image} alt='' />
                        </div>
                        <div className='cart-info-textbox'>

                          <div className='sub-item d-flex'>
                            <label className="cart-label">Product Name :</label>
                            <span className='cart-value'>{item.name}</span>
                          </div>

                          <div className='sub-item d-flex'>
                            <label className="cart-label">Price :</label>
                            <span className='cart-value'>₹ {item.cart_price.toFixed(2)}</span>
                          </div>

                          <div className='sub-item d-flex'>
                            <label className="cart-label">Quantity :</label>
                            <div className='cart-inputbox d-flex v-center'>
                              <button type='button' className='decr_minus' onClick={() => quantityCount([item.cart_id, item.quantity - 1])}>-</button>
                              <div className='cart_number'>{item.quantity}</div>
                              <button type='button' className='decr_minus' onClick={() => quantityCount([item.cart_id, item.quantity + 1])}>+</button>
                            </div>
                          </div>

                          <div className='sub-item d-flex'>
                            <label className="cart-label">Subtotal :</label>
                            <span className='cart-value'>₹ {item.sub_total.toFixed(2)}</span>
                          </div>

                          <div className='sub-item d-flex'>
                            <button type='button' className='btn primary small' onClick={() => removeItem(item.cart_id)}>Remove <i className="las la-trash ml-1"></i></button>
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                  */}

                  {/* ---------------------------------------------------------------
                      STANDALONE CART TOTAL - moved into the panel above as
                      .shop-cart-footer so it stays pinned while items scroll.
                      Kept here for easy restore: comment out the
                      <div className='shop-cart-footer'> block above, then
                      uncomment this one.
                  ---------------------------------------------------------------- */}
                  {/*
                  <div className='white_box cart-total-wrapper'>
                    <div className='box-title text-center'>
                      <h4>Cart Total</h4>
                    </div>

                    <div className='cart-total-list'>
                      <div className='sub-item d-flex'>
                        <label className="cart-label">Subtotal</label>
                        <span className='cart-value'>₹ {sum.toFixed(2)}</span>
                      </div>
                      <div className='sub-item d-flex'>
                        <label className="cart-label">GST</label>
                        <span className='cart-value'>₹ {gst.toFixed(2)}</span>
                      </div>
                      <div className='sub-item d-flex grand-total-item'>
                        <label className="cart-label"><strong>Total</strong></label>
                        <span className='cart-value'><strong>₹ {total.toFixed(2)}</strong></span>
                      </div>
                      <div className='sub-item d-flex grand-total-item'>
                        <span className='cart-label'>*Shipping fees will apply according to your delivery address.*</span>
                      </div>
                      <div className='sub-item d-flex form-btnbox'>
                        <Link className='btn secondary w-100' to='/checkout/'>Proceed to checkout <i className="las la-shopping-cart ml-2"></i></Link>
                      </div>
                    </div>
                  </div>
                  */}
                </Col>
              }

              {/* Mirrors the cart column: 9 (75%) when the cart is showing, 12
                  (full width) when it is hidden. PREVIOUS: 8 : 12

                  `key` (NEW) — this fixes a real bug, not a lint nit.
                  PREVIOUS: <Col xs={12} md={visibleItems.length !== 0 ? 9 : 12} lg={visibleItems.length !== 0 ? 9 : 12} className='right-block'>

                  Without a key, this Row's children alternate between
                  [false, Col] and [Col, Col] as the cart empties and fills,
                  because the cart column above renders `{cond && <Col/>}`.
                  React matches children by position, so going from two children
                  back to two children made it treat the existing medicine-grid
                  subtree as the new cart column's subtree and reuse it in the
                  wrong slot — carrying ProductData's state (its `addedIds` Set)
                  along with it. The grid then rendered as though every card had
                  already been added, and an "Add to Cart" click navigated to
                  /cart instead of adding. Distinct keys make the two branches
                  unmatchable, so no such reuse can occur. */}
              <Col key='medicine-grid' xs={12} md={visibleItems.length !== 0 ? 9 : 12} lg={visibleItems.length !== 0 ? 9 : 12} className='right-block'>
                <ProductData />
              </Col>
            </Row>

            {/* ---------------------------------------------------------------
                ORIGINAL SHOP PAGE MARKUP - kept for easy restore.
                Uncomment this and comment out the <Row className='cart-blocks'>
                block above to return to the plain medicine grid.
            ---------------------------------------------------------------- */}
            {/*
            <ProductData/>
            */}
          </div>
        </div>
      </section>

      {/* Checkout popup - opened by "Proceed to checkout" in the cart panel above.
          Gets the same items/totals the panel shows. Comment this out and restore
          the <Link to='/checkout/'> button to go back to the checkout page.

          CHANGED: mounted only while open. Previously it was always mounted with
          show={false}, which only hides the Bootstrap Modal - the component's
          effects still ran on every shop-page load and fired 'billing-address',
          'countries' and 'states' (three requests) for a popup nobody opened.
          Cart state is kept in this parent, so closing the popup loses nothing.
          PREVIOUS (kept for restore):
            <CheckoutPopup
              show={showCheckout}
              onHide={() => setShowCheckout(false)}
              items={visibleItems}
              sum={sum}
              gst={gst}
            />
      */}
      {showCheckout &&
        <CheckoutPopup
          show={showCheckout}
          onHide={() => setShowCheckout(false)}
          items={visibleItems}
          sum={sum}
          gst={gst}
        />
      }
    </div>
  )
}
export default Shop
